import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, DataTable, Layer, Pagination } from 'grommet';
import { Add } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { CreateReferralPartnerRequest, ReferralPartnerService } from '/src/api/services/ReferralPartnerService';
import { FooterNav, HeaderNav } from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import {
  Card,
  CardBody,
  CardHeader,
  DataTableHeader,
  DataTableItem,
  LoadingSpinner,
  Text,
} from '/src/components/common';
import { Input, SearchInput } from '/src/components/forms';
import type { ReferralPartner } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';

const FuseAdminReferral = observer(() => {
  const [searchQuery, setSearchQuery] = useState('');
  const [referralPartners, setReferralPartners] = useState<ReferralPartner[]>([]);
  const [filteredPartners, setFilteredPartners] = useState<ReferralPartner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');
  const [formData, setFormData] = useState<CreateReferralPartnerRequest>({
    name: '',
  });

  useEffect(() => {
    fetchReferralPartners();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPartners(referralPartners);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = referralPartners.filter((partner) => partner.name.toLowerCase().includes(query));
      setFilteredPartners(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, referralPartners]);

  const fetchReferralPartners = async () => {
    try {
      setIsLoading(true);
      const partners = await ReferralPartnerService.list();
      const excludedIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 13, 14, 15, 35];
      const filteredPartners = partners.filter((partner) => !excludedIds.includes(partner.id));
      setReferralPartners(filteredPartners);
      setFilteredPartners(filteredPartners);
    } catch (err) {
      console.error('Error fetching referral partners:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const paginatedPartners = filteredPartners.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNameError('Partner name is required');
      toast.error(toastMessages.createReferralPartner.requiredFields);
      return;
    }

    try {
      setIsSubmitting(true);
      await ReferralPartnerService.create(formData);
      toast.success(toastMessages.createReferralPartner.success);
      setIsModalVisible(false);
      setFormData({
        name: '',
      });
      setNameError('');
      fetchReferralPartners();
    } catch (error) {
      console.error('Error creating referral partner:', error);
      toast.error(toastMessages.createReferralPartner.error(error as ApiError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setFormData({
      name: '',
    });
    setNameError('');
  };

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                Referral Partners
              </Text>
            </Box>
            <Box align="end">
              <Button
                primary
                icon={<Add />}
                label="Add Partner"
                onClick={() => setIsModalVisible(true)}
                style={{
                  borderRadius: '8px',
                  fontWeight: 'normal',
                }}
              />
            </Box>

            <Card margin={{ vertical: 'small' }} background="white" elevation="none" round="xsmall">
              <CardHeader title="Referral List">
                <Box flex="grow" align="end">
                  <SearchInput
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    onSearch={handleSearch}
                    placeholder="Search partners..."
                  />
                </Box>
              </CardHeader>
              <CardBody pad="none">
                {isLoading ? (
                  <Box pad="large" align="center" justify="center" height="200px">
                    <LoadingSpinner />
                  </Box>
                ) : filteredPartners.length === 0 ? (
                  <Box pad="large" align="center">
                    <Text>No referral partners found</Text>
                  </Box>
                ) : (
                  <>
                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'name',
                          header: <DataTableHeader toUpperCase>Partner Name</DataTableHeader>,
                          size: '70%',
                          render: (datum: ReferralPartner) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.name}</DataTableItem>
                            </Box>
                          ),
                        },
                      ]}
                      data={paginatedPartners}
                      sortable
                      background={['light-6', 'white']}
                      border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                    />
                    {filteredPartners.length > itemsPerPage && (
                      <Box pad={{ horizontal: 'medium', vertical: 'small' }} align="center">
                        <Pagination
                          alignSelf="center"
                          size="small"
                          page={currentPage}
                          onChange={({ page }) => setCurrentPage(page)}
                          step={itemsPerPage}
                          numberItems={filteredPartners.length}
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>

      {isModalVisible && (
        <Layer
          position="center"
          onEsc={handleCloseModal}
          onClickOutside={handleCloseModal}
          responsive={false}
          style={{
            width: 'auto',
            maxWidth: 'min(95vw, 500px)',
            maxHeight: '95vh',
            overflow: 'auto',
            borderRadius: '8px',
          }}
        >
          <Card width="100%" style={{ width: '100%' }}>
            <CardHeader title="Add Referral Partner" />
            <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
              <Box gap="medium">
                <Input
                  label="Referral Partner Name"
                  value={formData.name}
                  setValue={(value) => {
                    setFormData({ ...formData, name: value });
                    if (nameError) setNameError('');
                  }}
                  placeholder="Enter partner name"
                  required
                  error={nameError}
                  style={{ width: '100%' }}
                />

                <Box direction="row" gap="small" justify="end" margin={{ top: 'small' }}>
                  <Button
                    label="Cancel"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                  <Button
                    primary
                    label={isSubmitting ? 'Creating...' : 'Create Partner'}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                </Box>
              </Box>
            </CardBody>
          </Card>
        </Layer>
      )}
    </Box>
  );
});

export default FuseAdminReferral;
